export type QualityDistribution = {
  oursStrong: number;
  oursNormal: number;
  othersStrong: number;
  othersNormal: number;
  poor: number;
  total: number;
};

export type PatentDisplayCountSource =
  | 'quality_distribution'
  | 'loaded_patents_count'
  | 'final_patents_count'
  | 'final_patents.length'
  | 'none';

export type PatentDisplayCountInfo = {
  count: number;
  source: PatentDisplayCountSource;
  qualityDistributionCount: number;
  loadedPatentsCount: number;
  finalPatentsCount: number;
  finalPatentsLength: number;
};

type PatentCountSessionLike = {
  loaded_patents_count?: unknown;
  final_patents_count?: unknown;
  final_patents?: unknown;
};

export const emptyQualityDistribution = (): QualityDistribution => ({
  oursStrong: 0,
  oursNormal: 0,
  othersStrong: 0,
  othersNormal: 0,
  poor: 0,
  total: 0,
});

export const toCount = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

export const sumQualityDistribution = (dist: QualityDistribution | null | undefined): number => {
  if (!dist) return 0;
  return (
    toCount(dist.oursStrong) +
    toCount(dist.oursNormal) +
    toCount(dist.othersStrong) +
    toCount(dist.othersNormal) +
    toCount(dist.poor)
  );
};

export const parseQualityDistribution = (raw: unknown): QualityDistribution | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const dist: QualityDistribution = {
    oursStrong: toCount(r.oursStrong),
    oursNormal: toCount(r.oursNormal),
    othersStrong: toCount(r.othersStrong),
    othersNormal: toCount(r.othersNormal),
    poor: toCount(r.poor),
    total: toCount(r.total),
  };
  const summed = sumQualityDistribution(dist);
  dist.total = dist.total || summed;
  return dist.total > 0 ? dist : null;
};

/**
 * Returns the same patent count the customer-facing results UI should display.
 *
 * `quality_distribution` is the most accurate value once quality evaluation has
 * been summarized. For heavy/offloaded sessions it can be absent while patent
 * search itself has already completed, so we fall back to persisted search
 * counts before using the legacy inline `final_patents.length`.
 */
export const getPatentDisplayCountInfo = (
  sessionData: PatentCountSessionLike | null | undefined,
  qualityDistribution?: QualityDistribution | null
): PatentDisplayCountInfo => {
  const data = sessionData || {};
  const qualityDistributionCount = sumQualityDistribution(qualityDistribution);
  const loadedPatentsCount = toCount(data.loaded_patents_count);
  const finalPatentsCount = toCount(data.final_patents_count);
  const finalPatentsLength = Array.isArray(data.final_patents) ? data.final_patents.length : 0;

  if (qualityDistributionCount > 0) {
    return {
      count: qualityDistributionCount,
      source: 'quality_distribution',
      qualityDistributionCount,
      loadedPatentsCount,
      finalPatentsCount,
      finalPatentsLength,
    };
  }

  if (loadedPatentsCount > 0) {
    return {
      count: loadedPatentsCount,
      source: 'loaded_patents_count',
      qualityDistributionCount,
      loadedPatentsCount,
      finalPatentsCount,
      finalPatentsLength,
    };
  }

  if (finalPatentsCount > 0) {
    return {
      count: finalPatentsCount,
      source: 'final_patents_count',
      qualityDistributionCount,
      loadedPatentsCount,
      finalPatentsCount,
      finalPatentsLength,
    };
  }

  if (finalPatentsLength > 0) {
    return {
      count: finalPatentsLength,
      source: 'final_patents.length',
      qualityDistributionCount,
      loadedPatentsCount,
      finalPatentsCount,
      finalPatentsLength,
    };
  }

  return {
    count: 0,
    source: 'none',
    qualityDistributionCount,
    loadedPatentsCount,
    finalPatentsCount,
    finalPatentsLength,
  };
};
